import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, Bus, ClipboardList, FilePenLine, Settings, Plus, ChevronUp, Wallet } from 'lucide-react';
import SSTLogo from './SSTLogo';
import { getRole } from "../../utils/auth";
import permissions from "../../utils/permissions";
import "./Sidebar.css";

// const user =
//   JSON.parse(
//     localStorage.getItem("user")
//   );

const NavItem = ({ to, name, Icon }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!to) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', padding: '12px 24px', gap: '12px',
          cursor: 'default', transition: 'all 0.2s', position: 'relative',
          textDecoration: 'none',
          backgroundColor: isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
          color: isHovered ? '#ffffff' : '#A0A5B0',
          fontWeight: 400,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Icon size={20} style={{ flexShrink: 0 }} />
        <span>{name}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', padding: '12px 24px', gap: '12px',
        cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
        textDecoration: 'none',
        backgroundColor: isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        color: isActive ? '#07A95B' : (isHovered ? '#ffffff' : '#A0A5B0'),
        fontWeight: isActive ? 600 : 400,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {({ isActive }) => (
        <>
          <Icon size={20} style={{ flexShrink: 0 }} />
          <span>{name}</span>
        </>
      )}

    </NavLink>

  );
};


const FabMenu = ({
  onAddClient,
  onAddVehicle,
  onUploadSheet,
  rolePermissions
}) => {


  const [open, setOpen] = useState(false);


  const actions = [
    {
      label: "Add Client",
      action: onAddClient,
      permission: "clients",
    },
    {
      label: "Add Vehicle",
      action: onAddVehicle,
      permission: "vehicles",
    },
    {
      label: "Add Sheet",
      action: onUploadSheet,
      permission: "addSheet",
    },
  ];


  return (
    <div style={{ position: 'absolute', top: '50%', transform: 'translateY(calc(-50% + 38px))', right: '-24px', zIndex: 10 }}>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
          <div style={{
            position: 'absolute', bottom: '56px', right: '0', zIndex: 11,
            backgroundColor: 'white', borderRadius: '8px', padding: '16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            display: 'flex', flexDirection: 'column', gap: '16px',
            minWidth: '120px'
          }}>
            {actions
              .filter((a) => rolePermissions?.[a.permission])
              .map((a, i) => (
                <div
                  key={i}
                  onClick={() => {
                    a.action && a.action();
                    setOpen(false);
                  }}
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    color: '#07A95B',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {a.label}
                </div>
              ))}
          </div>
        </>
      )}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#ffffff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {open ? <ChevronUp size={22} color="#1F242C" /> : <Plus size={24} color="#1F242C" />}
      </button>

    </div>
  );
};

const Sidebar = ({ onAddClient, onAddVehicle, onUploadSheet }) => {
  const role = getRole();

  const rolePermissions = permissions[role];
  const navItems = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: LayoutDashboard,
      permission: 'dashboard'
    },
    {
      name: 'Clients',
      to: '/clients',
      icon: Users,
      permission: 'clients'
    },
    {
      name: 'Vehicles Management',
      to: '/vehicles',
      icon: Bus,
      permission: 'vehicles'
    },
    {
      name: 'User Management',
      to: '/users',
      icon: Users,
      permission: 'users'
    },
    {
      name: 'Client Format',
      to: '/client-format',
      icon: ClipboardList,
      permission: 'clientFormat'
    },
    {
      name: 'SST Format',
      to: '/sst-format',
      icon: FilePenLine,
      permission: 'sstFormat'
    },
    {
      name: 'Client Claim',
      to: '/client-claim',
      icon: ClipboardList,
      permission: 'clientClaim'
    },
    {
      name: 'Driver Pay Slabs',
      to: '/driver-pay-slabs',
      icon: ClipboardList,
      permission: 'driverPaySlab'
    },
    {
      name: 'Payments',
      to: '/payments',
      icon: Wallet,
      permission: 'payments'
    },
    {
      name: 'Driver Payout',
      to: '/driver-payout',
      icon: Wallet,
      permission: 'driverPayout'
    },
    {
      name: 'Settings',
      to: '/settings',
      icon: Settings,
      permission: 'settings'
    }
  ];

  const filteredNavItems = navItems.filter(
    (item) => permissions[role]?.[item.permission]
  );

  return (
    <aside style={{ width: '260px', backgroundColor: '#1F242C', display: 'flex', flexDirection: 'column', position: 'relative', flexShrink: 0, color: '#A0A5B0', fontSize: '15px' }}>
      <div style={{ height: '60px', backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px', borderBottom: '1px solid #E8E8E8', borderRight: '1px solid #E8E8E8', boxSizing: 'border-box' }}>
        <SSTLogo width="120px" height="auto" />
      </div>

      <nav style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '12px 0', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {
          filteredNavItems.map((item) => (

            <NavItem

              key={item.name}

              to={item.to}

              name={item.name}

              Icon={item.icon}

            />

          ))

        }
      </nav>
      {rolePermissions?.fabMenu && (
        <FabMenu
          onAddClient={onAddClient}
          onAddVehicle={onAddVehicle}
          onUploadSheet={onUploadSheet}
          rolePermissions={rolePermissions}
        />
      )}
    </aside>
  );
};

export default Sidebar;
